import { type ApiError, CSRF_HEADER, type ValidationIssue } from "@nexxt/contracts";
import { LIVE_GRANT_HEADER } from "@nexxt/contracts/realtime";
import type { Hono } from "hono";
import { type ClientResponse, hc } from "hono/client";
import type { SuccessStatusCode } from "hono/utils/http-status";

// The browser side of the gateway (ADR-021, ADR-040): same origin, session cookie only, never a token. Every request
// carries its own x-request-id, which the gateway echoes and the error screens show for support.

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/** A failed call: the gateway's `ApiError` plus the request id to quote to support. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: ApiError["details"];
  readonly requestId: string | null;

  constructor(error: ApiError, requestId: string | null) {
    super(error.message);
    this.name = "ApiRequestError";
    this.status = error.status;
    this.code = error.code;
    this.details = error.details;
    this.requestId = requestId;
  }

  /** Every problem of a business validation (422, `details.issues`), by field; empty for any other error. */
  get issues(): ValidationIssue[] {
    const issues = this.status === 422 ? this.details?.issues : undefined;
    return Array.isArray(issues) ? (issues as ValidationIssue[]) : [];
  }
}

export interface FetchOptions {
  /** Called on 401: the session is missing, expired or revoked. */
  onUnauthenticated?: () => void;
  /** Tests pass a fake; the browser uses the global one. */
  fetch?: typeof fetch;
  /** The grant of a live room a read answered with (ADR-277): the app opens the room's socket with it (ADR-278). */
  onLiveGrant?: (grant: string) => void;
  /** The request id of a write the browser sends: a signal of the app's own write is not read again (ADR-278). */
  onWrite?: (requestId: string) => void;
}

/**
 * `fetch` for `hc`: request id, CSRF header on writes (the gateway also checks `Origin`), cookies of this origin; the
 * grants of the live rooms and the ids of the writes to whoever opens the sockets.
 */
export function gatewayFetch({
  onUnauthenticated,
  fetch: base = fetch,
  onLiveGrant,
  onWrite,
}: FetchOptions = {}): typeof fetch {
  return async (input, init) => {
    const request = new Request(input, init);
    const headers = new Headers(request.headers);
    if (!headers.has("x-request-id")) headers.set("x-request-id", crypto.randomUUID());
    if (!SAFE_METHODS.has(request.method)) {
      headers.set(CSRF_HEADER, "1");
      const requestId = headers.get("x-request-id") ?? "";
      // after the request: an error of the live client never fails the call (ADR-278)
      if (onWrite) queueMicrotask(() => onWrite(requestId));
    }
    const res = await base(new Request(request, { headers, credentials: "same-origin" }));
    if (res.status === 401) onUnauthenticated?.();
    const grant = res.headers.get(LIVE_GRANT_HEADER);
    if (grant && onLiveGrant) queueMicrotask(() => onLiveGrant(grant));
    return res;
  };
}

export interface AppClientOptions extends FetchOptions {
  /** The app origin; the browser's by default. */
  origin?: string;
}

/**
 * The client of an app on its gateway's routes: the app worker forwards /api/* (prefix removed) and /auth/* to the
 * gateway (ADR-040), so business routes live under /api and the login routes stay at the root.
 */
// biome-ignore lint/suspicious/noExplicitAny: the constraint of hono's `hc`, any app of any gateway
export function appClient<App extends Hono<any, any, any>>({
  origin = globalThis.location?.origin ?? "",
  ...options
}: AppClientOptions = {}) {
  const fetch = gatewayFetch(options);
  return { api: hc<App>(`${origin}/api`, { fetch }), root: hc<App>(origin, { fetch }) };
}

type SuccessBody<R> =
  R extends ClientResponse<infer T, infer S, "json"> ? (S extends SuccessStatusCode ? T : never) : never;

/** The body of a successful response, or an `ApiRequestError`: what TanStack Query functions return and throw. */
export async function unwrap<R extends ClientResponse<unknown, number, "json">>(
  response: R | Promise<R>,
): Promise<SuccessBody<R>> {
  const res = await response;
  if (res.ok) return (await res.json()) as SuccessBody<R>;
  throw await requestError(res);
}

/** What `unwrap` and `unwrapBlob` read of a response: the client of `hc` and `fetch` both give it. */
type Answer = Pick<Response, "ok" | "status" | "statusText" | "headers" | "blob"> & { json(): Promise<unknown> };

/** The bytes of a successful response that is a file (ADR-069), or the same `ApiRequestError` as `unwrap`. */
export async function unwrapBlob(response: Answer | Promise<Answer>): Promise<Blob> {
  const res = await response;
  if (res.ok) return res.blob();
  throw await requestError(res);
}

async function requestError(res: Answer): Promise<ApiRequestError> {
  const body = (await res.json().catch(() => null)) as { error?: ApiError } | null;
  return new ApiRequestError(
    body?.error ?? { status: res.status, code: "http_error", message: res.statusText || "Request failed" },
    res.headers.get("x-request-id"),
  );
}

/**
 * The options of a call of a Hono client that a query can cancel: `withSignal(signal)` as its second argument. A
 * query without a signal (a loader that awaits it) passes nothing.
 */
export const withSignal = (signal: AbortSignal | undefined) => ({ init: { ...(signal && { signal }) } });

/** A file the user chose, with the id it is stored under: given once, at the choice (ADR-069). */
export interface ChosenFile {
  fileId: string;
  file: File;
}

/** `PUT /files/files/{fileId}` on the client of a gateway: both have the route of the files worker (ADR-069). */
interface FilesRoute {
  $put(
    args: { param: { fileId: string }; query: { folder: string; filename: string } },
    options: { init: { body: File } },
  ): Promise<Answer>;
}

/**
 * A file to a folder of the files worker, before an operation of a domain names it by id: the id is the client's, so
 * a retry stores the same file again, not a second one.
 */
export async function putFile(route: FilesRoute, { fileId, file }: ChosenFile, folder: string): Promise<void> {
  const res = await route.$put({ param: { fileId }, query: { folder, filename: file.name } }, { init: { body: file } });
  if (!res.ok) throw await requestError(res);
}
